import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, Save, FileSignature, Download, RefreshCw, CalendarRange } from "lucide-react";
import { API, api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const btn = "rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10 px-5";
const TA = "rounded-none font-serif text-[15px] leading-relaxed";

/** Award-drafting workspace: one editable finding per framed issue, compiled into the final-award composer. */
export const AwardWorkspace = ({ caseId, onCompiled }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/cases/${caseId}/award-draft`).then(({ data }) => setD(data)).catch(() => {}); }, [caseId]);
  if (!d) return null;
  const setFinding = (i, v) => setD({ ...d, findings: d.findings.map((f, k) => (k === i ? { ...f, finding: v } : f)) });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/cases/${caseId}/award-draft`, d); setD(data); toast.success("Draft saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const compile = async () => {
    try {
      await api.put(`/cases/${caseId}/award-draft`, d);
      const { data } = await api.get(`/cases/${caseId}/award-draft/compiled`);
      onCompiled(data);
      toast.success("Award text compiled — review it in the composer, then pass the award");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="border border-border bg-card" data-testid="award-workspace">
      <div className="px-6 py-4 border-b border-border flex flex-wrap items-center gap-3">
        <Scale className="h-4 w-4 text-blue-900 dark:text-blue-400" /><p className="label-caps">Award workspace · issue-wise findings</p>
        {d.updated_at && <span className="text-[11px] text-muted-foreground">saved {fmtDateTime(d.updated_at)}</span>}
      </div>
      <div className="px-6 py-5 space-y-5 text-sm">
        {d.findings.length === 0 && <p className="text-muted-foreground" data-testid="award-no-issues">Issues appear here once the case summary has framed them (or record an issues order first).</p>}
        <Textarea rows={2} placeholder="Preamble (optional) — background and procedural history…" value={d.preamble || ""} onChange={(e) => setD({ ...d, preamble: e.target.value })} className={TA} data-testid="award-preamble-input" />
        {d.findings.map((f, i) => (
          <div key={i} className="border border-border p-4" data-testid={`finding-${i}`}>
            <p className="font-semibold">Issue {i + 1}: {f.issue}</p>
            <Textarea rows={4} placeholder="Finding, reasons and evidence relied upon…" value={f.finding || ""} onChange={(e) => setFinding(i, e.target.value)} className={`${TA} mt-2`} data-testid={`finding-input-${i}`} />
          </div>
        ))}
        <Textarea rows={3} placeholder="Operative part — relief granted, costs, interest…" value={d.operative || ""} onChange={(e) => setD({ ...d, operative: e.target.value })} className={TA} data-testid="award-operative-input" />
        <div className="flex flex-wrap gap-3">
          <Button onClick={save} disabled={busy} className={btn} data-testid="award-save-button"><Save className="h-3.5 w-3.5 mr-2" /> Save draft</Button>
          <Button onClick={compile} variant="outline" className="rounded-none uppercase tracking-widest text-xs h-10 px-5" data-testid="award-compile-button"><FileSignature className="h-3.5 w-3.5 mr-2" /> Compile into final award</Button>
        </div>
      </div>
    </section>
  );
};

/** Dated chronology extracted from pleadings and annexures — for tribunal and counsel. */
export const Chronology = ({ caseId, isArb }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/cases/${caseId}/chronology`).then(({ data }) => setC(data)).catch(() => {});
  useEffect(() => { load(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps
  const regen = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/cases/${caseId}/chronology/regenerate`); setC(data); toast.success(`${data.events.length} events extracted`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!c) return null;
  return (
    <section className="border border-border bg-card" data-testid="chronology-section">
      <div className="px-6 py-4 border-b border-border flex flex-wrap items-center gap-3">
        <CalendarRange className="h-4 w-4 text-blue-900 dark:text-blue-400" /><p className="label-caps">Chronology of events</p>
        {c.generated_at && <span className="text-[11px] text-muted-foreground">prepared {fmtDateTime(c.generated_at)}</span>}
        <div className="ml-auto flex items-center gap-4">
          {c.events.length > 0 && <a href={`${API}/cases/${caseId}/chronology.pdf`} target="_blank" rel="noreferrer" data-testid="chronology-pdf" className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400 hover:opacity-70"><Download className="h-3.5 w-3.5" /> PDF</a>}
          {isArb && <button onClick={regen} disabled={busy} data-testid="chronology-generate" className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground disabled:opacity-50"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> {c.events.length ? "Regenerate" : "Build chronology"}</button>}
        </div>
      </div>
      {c.events.length === 0 ? (
        <p className="px-6 py-5 text-sm text-muted-foreground" data-testid="chronology-empty">{busy ? "Reading the pleadings and annexures…" : "No chronology yet."}</p>
      ) : (
        <table className="w-full text-sm" data-testid="chronology-table">
          <tbody>
            {c.events.map((e, i) => (
              <tr key={i} className="border-b border-border last:border-0" data-testid={`chronology-row-${i}`}>
                <td className="px-6 py-2 font-mono text-xs whitespace-nowrap align-top w-28">{e.date}</td>
                <td className="px-2 py-2 align-top">{e.event}</td>
                <td className="px-6 py-2 text-xs text-muted-foreground whitespace-nowrap align-top">{e.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};
